import { useContext, useEffect, useState, useRef } from "react";

import { Button, Center, Fade, Flex, useDisclosure } from "@chakra-ui/react";
import { useSession } from "next-auth/react";
import PuffLoader from "react-spinners/PuffLoader";
import { useRouter } from "next/dist/client/router";

import Tweets from "components/tweets";
import ProtectedRoute from "components/protected-route";
import SubscribePopup from "components/subscribe-popup";
import { useOnboardingSteps } from "components/onboard-panel/useOnboardingSteps";
import { Page } from "components/page";
import { useStats } from "hooks/useStats";
import { ConfirmPopup } from "../components/popups/confirmPopup";
import { MainContext } from "../context/mainContext";
import { getList } from "../controllers/bookmark";
import { search } from "../controllers/search";
import { getAccount, updateUser } from "../utils/sessionHelper";
import { useFirebaseUser } from "../utils/useFirebaseUser";
import { SettingsFilters } from "../components/settings-filter";
import { lastInputDateOptions } from "../components/settings-filter/static-values";
import { FiSettings } from "react-icons/fi";
import { RefreshIcon } from "components/icons/refresh";
import { getCachedKey, getCachedKeyTime, updateCachedKey, updateCachedKeyTime } from "../utils/localforageHelpers";

export const Application = () => {
  const { data: session, status } = useSession() ?? {};
  const loading = status === "loading";
  const [savedTweet, setSavedTweet] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const [data, setData] = useState<any>(null);

  const router = useRouter();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [excludedUsers, setExcludedUsers] = useState<Array<string>>([]);
  const { isOpen: isSettingsOpen, onToggle: onToggleSettings } =
    useDisclosure();

  const [noHashtag, setNoHashtag] = useState(0);
  const [includeCarousel, setIncludeCarousel] = useState(0);
  const [plateformFilter, setPlateformFilter] = useState(0);

  const [refresh, refresher] = useState({});
  const [hasFetched, setHasFetched] = useState(true);
  const [popupMode, setPopupMode] = useState("fail");
  const isAuthenticated = useFirebaseUser();
  const { updateOnboardSteps } = useOnboardingSteps();
  const mainContext = useContext(MainContext);
  const { updateStatsCounter } = useStats();


  const [nbMaxDaysOld, setNbMaxDaysOld] = useState(lastInputDateOptions[1]);
  const [countrySelected, setCountrySelected] = useState<Array<string>>([]);

  const [cachedPostsLoaded, setCachedPostsLoaded] = useState(false);
  const [changeRefreshButtonState, setChangeRefreshButtonState] =
    useState(false);
  const clearCache = useRef(false);

  let params = {
    isPersonalizedSorting: true,
    noCrypto: true,
    noDollar: true,
    nbMinLikes: 100,
    disableTwitter: plateformFilter === -1,
    disableLinkedin: plateformFilter === 1,
    includeCarousel,
    ...(noHashtag !== -1 && {
      noHashtag: !!noHashtag,
    }),
    excludedUsers,
    ...(countrySelected.length > 0 &&
      countrySelected.length !== 258 && {
      authorCountry: countrySelected,
    }),
    ...(parseInt(nbMaxDaysOld.value) > 0 && {
      nbMinDaysOld: 1,
      nbMaxDaysOld: parseInt(nbMaxDaysOld.value),
    }),
  };

  useEffect(() => {
    if (isAuthenticated && getAccount(session)?.id) {
      getAccount(session)?.hastagFilter &&
        setNoHashtag(getAccount(session)?.hastagFilter);
      getAccount(session)?.carrouselFilter &&
        setIncludeCarousel(getAccount(session)?.carrouselFilter);
      getAccount(session)?.plateformFilter &&
        setPlateformFilter(getAccount(session)?.plateformFilter);
      getAccount(session)?.countryFilter &&
        setCountrySelected(getAccount(session)?.countryFilter);

      const defaultMaximumDateFilterValue = lastInputDateOptions.find(
        (option) =>
          option.value === getAccount(session)?.nbMaxDaysOld?.toString()
      );
      defaultMaximumDateFilterValue &&
        setNbMaxDaysOld(defaultMaximumDateFilterValue);

      getAccount(session)?.exludedUsers &&
        setExcludedUsers(getAccount(session)?.exludedUsers);
      setHasFetched(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated && (!hasFetched || clearCache?.current === true)) {
      sendRequest(session);
    }
  }, [session, isAuthenticated, params, clearCache]);

  useEffect(() => {
    if (excludedUsers?.length === 0) {
      return;
    }

    const updateExcludedUsers = async () => {
      await updateUser(
        session,
        {
          exludedUsers: excludedUsers,
        },
        false,
        true
      );
    };
    updateExcludedUsers();
  }, [excludedUsers]);

  const getSavedTweets = async () => {
    if (!loading && session?.user?.uid) {
      const savedTweets = await getList(getAccount(session).id);
      setSavedTweet(savedTweets);
    }
  };

  useEffect(() => {
    getSavedTweets();
  }, [refresh]);

  // const sendRequest = React.useCallback(async (session, topic) => {
  const sendRequest = async (session) => {
    if (isSending) return;
    setIsSending(true);
    setChangeRefreshButtonState(true);
    console.log("sendRequest");
    let cachedPosts: any = await getCachedKey("cachedViralPosts");
    let cachedPostsTime = await getCachedKeyTime("cachedViralPostsTime");
    const aDayFromNow = new Date().getTime() + 24 * 60 * 60 * 1000;

    if (Boolean(cachedPostsTime) === false) {
      await updateCachedKeyTime("cachedViralPostsTime", aDayFromNow);
    }

    if (
      new Date().getTime() <= new Date(cachedPostsTime).getTime() &&
      !clearCache.current
    ) {
      if (Boolean(cachedPosts) === true && cachedPosts?.tweets?.length > 0) {
        console.log("cache hit");
        setCachedPostsLoaded(true);
        setData(cachedPosts);
        setHasFetched(true);
        setIsSending(false);
        setChangeRefreshButtonState(false);
        return;
      } else {
        console.log("cache miss");
        setCachedPostsLoaded(false);
      }
    }

    if (!cachedPostsLoaded || clearCache.current) {
      console.log("caching...");
      let keywords = getAccount(session)?.keywords;
      if (
        !getAccount(session)?.keywords ||
        !getAccount(session)?.keywords.join
      ) {
        setPopupMode("fail");
      }

      if (keywords?.join) {
        //@ts-ignore
        var data = await search(
          session,
          keywords.join(","),
          params,
          "auto",
          mainContext
        );
        updateStatsCounter("search_via_auto_page");
      } else {
        setPopupMode("fail");
        onOpen();
      }
      setHasFetched(true);

      if (!data || data.success === 0) {
        setData(null);
      } else {
        setData({ ...data });
        await updateCachedKey("cachedViralPosts", data);
        clearCache.current = false;
        await updateCachedKeyTime("cachedViralPostsTime", aDayFromNow);
      }
    }

    updateOnboardSteps("aiInpiration", "searchInspiration");
    setIsSending(false);
    setChangeRefreshButtonState(false);
  };

  // const tweetContext: any = useContext(TweetContext);

  if (!loading && !session) {
    return <ProtectedRoute />;
  }


  return (
    <Page
      title="Post Inspirations"
      description="Use these high-performing posts as inspirations for your next content! Our AI engine selected these for you."
      infoTooltipText="These posts were selected for you by our AI engine, based on your parameters set in the settings section."
      isLoadingFeature={isSending}
      showLoadingBar={true}
    >
      <Flex gap="3" mt="8" justifyContent="flex-end">
        <Button
          m={0}
          isDisabled={!session?.user?.uid}
          leftIcon={<FiSettings />}
          variant="secondary"
          borderColor="0A66C2"
          sx={{
            _disabled: {
              bg: "white",
              // color: "gray.600",
              cursor: "not-allowed",
            },
          }}
          onClick={onToggleSettings}
        >
          Filters
        </Button>
        <Button
          m={0}
          isDisabled={
            !session?.user?.uid || isSending || changeRefreshButtonState
          }
          sx={{
            _disabled: {
              bg: "white",
              // color: "gray.600",
              cursor: "not-allowed",
            },
          }}
          leftIcon={<RefreshIcon isLoading={changeRefreshButtonState} />}
          variant="secondary"
          borderColor="0A66C2"
          onClick={() => {
            clearCache.current = true;
            //@ts-ignore
            sendRequest && sendRequest(session);
          }}
        >
          {changeRefreshButtonState ? "Refreshing..." : "Refresh"}
        </Button>
      </Flex>
      {isSettingsOpen && (
        <Fade in={isSettingsOpen}>
          <SettingsFilters
            setNoHashtag={setNoHashtag}
            noHashtag={noHashtag}
            setPlateformFilter={setPlateformFilter}
            plateformFilter={plateformFilter}
            setIncludeCarousel={setIncludeCarousel}
            includeCarousel={includeCarousel}
            setHasFetched={setHasFetched}
            exludedUsers={excludedUsers}
            setExcludedUsers={setExcludedUsers}
            setMaximumDateFilter={setNbMaxDaysOld}
            setCountrySelected={setCountrySelected}
            countrySelected={countrySelected}
            maximumDateFilter={nbMaxDaysOld}
            closeSettings={onToggleSettings}
            clearCache={clearCache}
          />
        </Fade>
      )}
      {isSending && !clearCache.current && (
        <Center pt={10}>
          <PuffLoader
            color={"#0A66C2"}
            loading={isSending}
            cssOverride={{
              display: "block",
              margin: "0 auto",
              borderColor: "red",
            }}
            size={50}
          />
        </Center>
      )}
      {(!isSending || clearCache.current) && data?.tweets && (
        <Tweets
          sendRequest={sendRequest}
          savedTweet={savedTweet}
          tweets={data.tweets}
          data={data}
          setData={setData}
          setExcludedUsers={setExcludedUsers}
          disableBlur={false}
          disableBookmarkButton={false}
          refresher={refresher}
          refresh={refresh}
          disableSaveCsv={true}
          disableEmbed={true}
          disableTryAgain={false}
          disableRefresh={true}
          disableDescriptionWarning={false}
          source="ez4cast"
        />
      )}

      {!loading &&
        data?.tweets &&
        !session?.user.data?.subscription?.isSubscribed &&
        (!session?.user.data?.remainingFreeSearch ||
          session.user.data.remainingFreeSearch <= 0) && <SubscribePopup />}
      <ConfirmPopup
        isOpen={
          isOpen
          && Boolean(session?.user?.data?.subStatus) === true
        } //change this
        onClose={onClose}
        title="We couldn't find your search settings"
        body={"Please, fill in your search settings to use this feature."}
        cta="Set my search settings"
        callback={async () => {
          router.push("/onboarding");
        }}
      />
    </Page>
  );
};

export default Application;
